import './store_monitoring.css'

import { useMetadata } from '@api/useMetadata'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useLocalStorage } from '@gravitate-js/the-armory'
import React, { createContext, useContext, useEffect, useState } from 'react'

import { useUserSettings } from '@hooks/settings'
import { OrderModal } from '../OrderWizard/OrderModal'

export const defaultSiteMonitoringFilters = {
  has_order: 'none',
  match_only_tank: false,
  view: 'all',
  alerts: [],
}

const SiteMonitoringContext = createContext()
export function SiteMonitoringContextProvider({ children }) {
  const [showingModal, setShowingModal] = useState()
  const [selectedOrderId, setSelectedOrderId] = useState()
  const [preFillStore, setPrefillStore] = useState()
  const { data: filterOptions } = useMetadata('store_monitor')
  const { useSiteMonitoringOverviewQuery } = useSiteMonitoring()
  const { updateSetting, getSetting } = useUserSettings()
  const { value: detailStoreIds, setValue: settingDetailStoreIds } = useLocalStorage(
    'site-monitoring-selected-stores',
    []
  )
  const { value: currentSiteIndex, setValue: setCurrentSiteIndex } = useLocalStorage(
    'site-monitoring-current-site-index',
    0
  )
  const setDetailStoreIds = (newIds) => {
    settingDetailStoreIds(newIds)
    updateSetting('site-monitoring-selected-stores', newIds)
  }
  const { value: filters, setValue: settingFilters } = useLocalStorage(
    'site_monitoring_server_query_filters',
    defaultSiteMonitoringFilters
  )
  const setFilters = (newFilters) => {
    settingFilters(newFilters)
    updateSetting('site_monitoring_server_query_filters', newFilters)
  }
  const { value: sortedInfo, setValue: settingSortedInfo } = useLocalStorage('site-monitoring-tank-sorted-info', {
    columnKey: 'tanks_ids',
    order: 'ascend',
  })
  const setSortedInfo = (newSortedInfo) => {
    settingSortedInfo(newSortedInfo)
    updateSetting('site-monitoring-tank-sorted-info', newSortedInfo)
  }

  const { data, isPending, isFetching: isBackgroundRefreshing, status } = useSiteMonitoringOverviewQuery(filters)
  const { value: customSelectedStores, setValue: settingCustomSelectedStores } = useLocalStorage(
    'site-monitoring-custom-search',
    []
  )
  const setCustomSelectedStores = (newStores) => {
    settingCustomSelectedStores(newStores)
    updateSetting('site-monitoring-custom-search', newStores)
  }

  useEffect(() => {
    getSetting('site-monitoring-custom-search').then((res) => res)
  }, [])

  const [filteredStores, setFilteredStores] = useState(data?.stores || [])

  useEffect(() => {
    if (!customSelectedStores?.length) {
      setFilteredStores(data?.stores || [])
    } else {
      const labelList = customSelectedStores.map((s) => s.label)
      setFilteredStores(data?.stores.filter((store) => labelList.includes(store.store_number)) || [])
    }
  }, [data, customSelectedStores])

  const showModal = (orderId) => {
    setSelectedOrderId(orderId)
    setShowingModal(true)
  }
  const showModalWithStore = (store, orderId) => {
    setPrefillStore(store)
    showModal(orderId)
  }
  const closeModal = () => {
    setShowingModal(false)
    setSelectedOrderId(undefined)
    setPrefillStore(undefined)
  }

  return (
    <SiteMonitoringContext.Provider
      value={{
        showModal,
        closeModal,
        filters,
        setFilters,
        filterOptions,
        data,
        filteredStores,
        setFilteredStores,
        loading: isPending,
        isBackgroundRefreshing,
        showModalWithStore,
        showingModal,
        detailStoreIds,
        setDetailStoreIds,
        sortedInfo,
        setSortedInfo,
        setCustomSelectedStores,
        customSelectedStores,
        currentSiteIndex,
        setCurrentSiteIndex,
        preFillStore,
        setPrefillStore,
      }}
    >
      <OrderModal
        showingModal={showingModal}
        closeModal={closeModal}
        selectedOrder={selectedOrderId}
        defaultStore={preFillStore}
      />

      {children}
    </SiteMonitoringContext.Provider>
  )
}

export const useSiteMonitoringContext = () => {
  const context = useContext(SiteMonitoringContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
